import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Chip,
  Button,
  Grid,
  CircularProgress
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RefreshIcon from '@mui/icons-material/Refresh';
import { APIURL } from 'src/config';
import axios from 'axios';

const EvvReadiness = ({ user, comp_id, checkTokenExpiry }) => {
  const [loading, setLoading] = useState(false);
  const [readinessData, setReadinessData] = useState(null);
  const [error, setError] = useState(null);

  // Transform API response to component format
  const transformApiData = (apiData) => {
    // Map overall status
    const overallStatusMap = {
      'Issues Found': 'warning',
      'Ready for EVV': 'success',
      'Critical Issues': 'error'
    };

    // Map employee issues with id field for DataGrid
    const employeeIssues = apiData.employee_issues?.map((emp) => ({
      id: emp.emp_id,
      name: emp.emp_name,
      empId: emp.emp_id,
      issues: emp.issues || [],
      severity: (emp.severity || 'medium').toLowerCase()
    })) || [];

    // Map customer issues with id field for DataGrid
    const customerIssues = apiData.customer_issues?.map((cus) => ({
      id: cus.cus_id,
      name: cus.cus_name,
      cusId: cus.cus_id,
      issues: cus.issues || [],
      severity: (cus.severity || 'medium').toLowerCase()
    })) || [];

    // Map task type issues with id field for DataGrid
    const taskTypeIssues = apiData.task_type_issues?.map((task, index) => ({
      id: task.task_type_id || index,
      name: task.task_type_name || 'N/A',
      issues: task.issues || [],
      severity: (task.severity || 'medium').toLowerCase()
    })) || [];

    return {
      summary: {
        totalEmployees: apiData.summary?.employees?.total || 0,
        employeesWithIssues: apiData.summary?.employees?.with_issues || 0,
        totalCustomers: apiData.summary?.customers?.total || 0,
        customersWithIssues: apiData.summary?.customers?.with_issues || 0,
        totalTaskTypes: apiData.summary?.task_types?.total || 0,
        taskTypesWithIssues: apiData.summary?.task_types?.with_issues || 0,
        overallStatus: overallStatusMap[apiData.overall_status] || 'info'
      },
      employeeIssues,
      customerIssues,
      taskTypeIssues
    };
  };

  // Fetch data from API
  const fetchReadinessData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${APIURL}/evv/nj/readiness?company_id=${comp_id}`);
      const transformedData = transformApiData(response.data);
      setReadinessData(transformedData);
    } catch (error) {
      console.error('Error fetching readiness data:', error);
      setError('Failed to load EVV readiness data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [comp_id]);

  useEffect(() => {
    fetchReadinessData();
  }, [fetchReadinessData]);

  const handleRefresh = () => {
    fetchReadinessData();
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'high': return <ErrorIcon />;
      case 'medium': return <WarningIcon />;
      case 'low': return <WarningIcon />;
      default: return <CheckCircleIcon />;
    }
  };

  const getOverallStatusColor = (status) => {
    switch (status) {
      case 'success': return 'success';
      case 'warning': return 'warning';
      case 'error': return 'error';
      default: return 'info';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Running EVV Readiness Check...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box textAlign="center" py={4}>
        <Alert severity="error" sx={{ mb: 2, maxWidth: '600px', mx: 'auto' }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={handleRefresh} sx={{ mt: 2 }}>
          Retry
        </Button>
      </Box>
    );
  }

  if (!readinessData) {
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="h6">No data available</Typography>
        <Button variant="contained" onClick={handleRefresh} sx={{ mt: 2 }}>
          Run EVV Readiness Check
        </Button>
      </Box>
    );
  }

  return (
    <Box m={2}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <h4>EVV Readiness Report</h4>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading}
        >
          Refresh Report
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={3}>
          <Card sx={{ height: '140px', display: 'flex', alignItems: 'center' }}>
            <CardContent sx={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>Overall Status</Typography>
              <Alert severity={getOverallStatusColor(readinessData.summary.overallStatus)} sx={{ width: '100%' }}>
                {readinessData.summary.overallStatus === 'success' && 'Ready for EVV'}
                {readinessData.summary.overallStatus === 'warning' && 'Issues Found'}
                {readinessData.summary.overallStatus === 'error' && 'Critical Issues'}
              </Alert>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ height: '140px', display: 'flex', alignItems: 'center' }}>
            <CardContent sx={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>Employees</Typography>
              <Typography variant="h5" sx={{ mb: 0.5 }}>
                {readinessData.summary.employeesWithIssues}/{readinessData.summary.totalEmployees}
              </Typography>
              <Typography variant="body2">with issues</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ height: '140px', display: 'flex', alignItems: 'center' }}>
            <CardContent sx={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>Customers</Typography>
              <Typography variant="h5" sx={{ mb: 0.5 }}>
                {readinessData.summary.customersWithIssues}/{readinessData.summary.totalCustomers}
              </Typography>
              <Typography variant="body2">with issues</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ height: '140px', display: 'flex', alignItems: 'center' }}>
            <CardContent sx={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>Task Types</Typography>
              <Typography variant="h5" sx={{ mb: 0.5 }}>
                {readinessData.summary.taskTypesWithIssues}/{readinessData.summary.totalTaskTypes}
              </Typography>
              <Typography variant="body2">with issues</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Detailed Issues */}
      <Box>
        {/* Employee Issues */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">
              Employee Issues ({readinessData.employeeIssues.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <DataGrid
              rows={readinessData.employeeIssues}
              columns={[
                { field: 'name', headerName: 'Employee Name', width: 200 },
                { field: 'empId', headerName: 'Employee ID', width: 150 },
                {
                  field: 'issues',
                  headerName: 'Issues',
                  width: 400,
                  renderCell: (params) => (
                    <Box>
                      {params.value.map((issue, index) => (
                        <Chip
                          key={index}
                          label={issue}
                          size="small"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                    </Box>
                  )
                },
                {
                  field: 'severity',
                  headerName: 'Severity',
                  width: 150,
                  renderCell: (params) => (
                    <Chip
                      icon={getSeverityIcon(params.value)}
                      label={params.value.toUpperCase()}
                      color={getSeverityColor(params.value)}
                      size="small"
                    />
                  )
                }
              ]}
              pageSize={5}
              rowsPerPageOptions={[5, 10, 25]}
              disableSelectionOnClick
              autoHeight
              getRowHeight={() => 'auto'}
              sx={{
                '& .MuiDataGrid-cell': {
                  display: 'flex',
                  alignItems: 'center',
                  paddingY: 1
                }
              }}
            />
          </AccordionDetails>
        </Accordion>

        {/* Customer Issues */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">
              Customer Issues ({readinessData.customerIssues.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <DataGrid
              rows={readinessData.customerIssues}
              columns={[
                { field: 'name', headerName: 'Customer Name', width: 200 },
                { field: 'cusId', headerName: 'Customer ID', width: 130 },
                {
                  field: 'issues',
                  headerName: 'Issues',
                  width: 400,
                  renderCell: (params) => (
                    <Box>
                      {params.value.map((issue, index) => (
                        <Chip
                          key={index}
                          label={issue}
                          size="small"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                    </Box>
                  )
                },
                {
                  field: 'severity',
                  headerName: 'Severity',
                  width: 150,
                  renderCell: (params) => (
                    <Chip
                      icon={getSeverityIcon(params.value)}
                      label={params.value.toUpperCase()}
                      color={getSeverityColor(params.value)}
                      size="small"
                    />
                  )
                }
              ]}
              pageSize={5}
              rowsPerPageOptions={[5, 10, 25]}
              disableSelectionOnClick
              autoHeight
              getRowHeight={() => 'auto'}
              sx={{
                '& .MuiDataGrid-cell': {
                  display: 'flex',
                  alignItems: 'center',
                  paddingY: 1
                }
              }}
            />
          </AccordionDetails>
        </Accordion>

        {/* Task Type Issues */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">
              Task Type Issues ({readinessData.taskTypeIssues.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <DataGrid
              rows={readinessData.taskTypeIssues}
              columns={[
                { field: 'name', headerName: 'Task Type Name', width: 200 },
                {
                  field: 'issues',
                  headerName: 'Issues',
                  width: 400,
                  renderCell: (params) => (
                    <Box>
                      {params.value.map((issue, index) => (
                        <Chip
                          key={index}
                          label={issue}
                          size="small"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                    </Box>
                  )
                },
                {
                  field: 'severity',
                  headerName: 'Severity',
                  width: 150,
                  renderCell: (params) => (
                    <Chip
                      icon={getSeverityIcon(params.value)}
                      label={params.value.toUpperCase()}
                      color={getSeverityColor(params.value)}
                      size="small"
                    />
                  )
                }
              ]}
              pageSize={5}
              rowsPerPageOptions={[5, 10, 25]}
              disableSelectionOnClick
              autoHeight
              getRowHeight={() => 'auto'}
              sx={{
                '& .MuiDataGrid-cell': {
                  display: 'flex',
                  alignItems: 'center',
                  paddingY: 1
                }
              }}
            />
          </AccordionDetails>
        </Accordion>
      </Box>
    </Box>
  );
};

export default EvvReadiness;
